import type { InstanceMember, UserSummary } from "@keydials/api-client";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "@keydials/dial-workspace";

/**
 * Turning a dial's grants into the rows a Members table shows (KEY-454).
 *
 * The shape mismatch this exists to fix: `detail.members` is one entry per **grant**, and a person may
 * hold several at different scopes. The old modal listed grants directly, so somebody with access to three
 * sections appeared three times with no indication they were one person. A table sorted by name would make
 * that actively confusing, so rows are people and grants are folded into a summary.
 */

export type MemberSortKey = "name" | "email" | "role" | "scope";
export type SortDirection = "asc" | "desc";

export interface MemberRow {
  userId: string;
  /** Display name where there is one, otherwise the email — never blank, so a sort is never arbitrary. */
  name: string;
  email: string;
  /** Every grant this person holds on the dial, in the order the server returned them. */
  grants: InstanceMember[];
  /** Highest-authority role across their grants, which is what actually governs what they can do. */
  role: string;
  /** "Whole dial", or a short description of how many parts they can reach. */
  scope: string;
  /** Holds at least one grant covering the whole dial (no section and no segment). */
  wholeDial: boolean;
  /** An outside invite rather than a member of the organisation. */
  isGuest: boolean;
  /**
   * True when nothing about this person can be changed from this page: every grant they hold is either
   * inherited from a parent group (managed on the group) or in the Admin tier this caller may not alter.
   */
  locked: boolean;
}

/** Most authority first. Mirrors the flat [Flags] InstanceRole: this is a display ordering, not a hierarchy. */
const ROLE_RANK: Record<string, number> = { Admin: 0, Editor: 1, Reviewer: 2, Reader: 3 };

function rankOf(role: string): number {
  return ROLE_RANK[role] ?? 99;
}

function scopeOf(grants: InstanceMember[], noun: SegmentNoun): { scope: string; wholeDial: boolean } {
  if (grants.some((g) => !g.sectionKey && !g.segmentKey)) {
    return { scope: "Whole dial", wholeDial: true };
  }
  const sections = new Set(grants.map((g) => g.sectionKey).filter(Boolean));
  const segments = grants.filter((g) => g.segmentKey).length;
  if (segments > 0) {
    // KEY-539: a one/many pick in the dial's own words. Never `${noun.one}s` — see vocabulary.ts.
    return { scope: `${segments} ${segments === 1 ? noun.one : noun.many}`, wholeDial: false };
  }
  const n = sections.size;
  return { scope: n === 1 ? "1 section" : `${n} sections`, wholeDial: false };
}

export interface MemberRowOptions {
  /** User ids that came in through a guest invite rather than organisation membership. */
  guestUserIds?: ReadonlySet<string>;
  /** Whether this caller may alter Admin-tier grants (org instance-managers only). */
  canGrantAdmin: boolean;
  /** This dial's word for an action point (KEY-539). Carried in the options bag the function already takes
   *  rather than as a third positional parameter; omitted means the platform's word, so every existing
   *  caller and test keeps today's wording untouched. */
  noun?: SegmentNoun;
}

/** One row per person, grants folded in. */
export function memberRows(members: readonly InstanceMember[], opts: MemberRowOptions): MemberRow[] {
  const byUser = new Map<string, InstanceMember[]>();
  for (const m of members) {
    const list = byUser.get(m.userId);
    if (list) {
      list.push(m);
    } else {
      byUser.set(m.userId, [m]);
    }
  }

  const rows: MemberRow[] = [];
  for (const [userId, grants] of byUser) {
    const first = grants[0];
    const best = [...grants].sort((a, b) => rankOf(a.role) - rankOf(b.role))[0];
    const { scope, wholeDial } = scopeOf(grants, opts.noun ?? DEFAULT_SEGMENT_NOUN);
    // Locked only when EVERY grant is untouchable — one editable grant is reason to offer the row.
    const locked = grants.every((g) => g.inherited === true || (g.role === "Admin" && !opts.canGrantAdmin));
    rows.push({
      userId,
      name: first.displayName?.trim() || first.email,
      email: first.email,
      grants,
      role: best.role,
      scope,
      wholeDial,
      isGuest: opts.guestUserIds?.has(userId) ?? false,
      locked,
    });
  }
  return rows;
}

/**
 * Sorted rows. Always breaks ties on name so the order is total — an unstable table that reshuffles rows
 * when nothing changed reads as a bug.
 */
export function sortMemberRows(rows: readonly MemberRow[], key: MemberSortKey, dir: SortDirection): MemberRow[] {
  const sign = dir === "asc" ? 1 : -1;
  const byName = (a: MemberRow, b: MemberRow) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  return [...rows].sort((a, b) => {
    let cmp = 0;
    if (key === "name") cmp = byName(a, b);
    else if (key === "email") cmp = a.email.localeCompare(b.email, undefined, { sensitivity: "base" });
    else if (key === "role") cmp = rankOf(a.role) - rankOf(b.role);
    else if (key === "scope") cmp = Number(b.wholeDial) - Number(a.wholeDial) || a.scope.localeCompare(b.scope);
    return cmp !== 0 ? cmp * sign : byName(a, b);
  });
}

/**
 * The "Give access to" picker, minus anyone who already has access (KEY-454).
 *
 * Filtered here rather than server-side on purpose. The picker is for people with **no** access at all;
 * giving an existing member an additional scoped grant is a different act, done from their own row. A
 * server-side filter on `listAssignableInstanceMembers` would close that second path off too.
 */
export function assignableExcludingMembers(
  assignable: readonly UserSummary[],
  members: readonly InstanceMember[],
): UserSummary[] {
  const held = new Set(members.map((m) => m.userId));
  return assignable.filter((u) => !held.has(u.id));
}
